// src/pages/Home/Home.js
import React, { useMemo, useState, useEffect, useRef, useContext } from "react";
import { Tabs, message } from "antd";
import PageLayout from "../../components/PageLayout";
import homeHelp from "../../content/help/home.json";
import { getModuleGroups } from "./Constants";
import ModuleGrid from "./Components/ModuleGrid";
import { createContainer } from "./Utils/CreateContainerAPI";
import { toggleStatus } from "./Utils/ToggleStatusAPI";
import { deleteInstance } from "./Utils/DeleteInstanceAPI";
import {
  getAllInstancesMinimal,
  subscribeToInstancesMinimal,
} from "./Utils/GetAllInstancesAPI";
import usePageTitle from "../../services/usePageTitle";
import { useTheme } from "../../services/useTheme";
import { AuthContext } from "../../services/authcontext";

const TitleMapping = {
  // Used by the Grid (frontend IDs)
  skeyenote: "SKEYE NOTE",
  skeyecharts: "SKEYE CHARTS",
  skeyeingest: "SKEYE INGEST",
  skeyecube: "SKEYE CUBE",
  skeyeform: "SKEYE FORM",
  skeyepredict: "SKEYE PREDICT",
  hadoop: "Hadoop",
  spark: "Spark",
  hive: "Hive",
  dghelper: "DG Helper",
  fahras: "Fahras",
  jeyeda: "Jeyeda",

  // Used by the Backend JSON parser
  Skeyenote: "SKEYE NOTE",
  Skeyechart: "SKEYE CHARTS",
  Skeyeingest: "SKEYE INGEST",
  Skeyecube: "SKEYE CUBE",
  Skeyeform: "SKEYE FORM",
  Skeyepredict: "SKEYE PREDICT",
  Hadoop: "Hadoop",
  Spark: "Spark",
  Hive: "Hive",
  DGHelper: "DG Helper",
  Fahras: "Fahras",
  Jeyeda: "Jeyeda",
};

function Home({ pageTitle = "Home" }) {
  usePageTitle("Home - SkeyeGate™");
  const [cardStatus, setCardStatus] = useState({});
  const [containerIds, setContainerIds] = useState({});
  const [exposedPorts, setExposedPorts] = useState({});
  const [loading, setLoading] = useState({});
  const [initialLoading, setInitialLoading] = useState(true);
  const [isBackendDown, setIsBackendDown] = useState(false);
  const isDarkTheme = useTheme();

  const [messageApi, contextHolder] = message.useMessage();

  const [wsConnected, setWsConnected] = useState(false);
  const pollingIntervalRef = useRef(null);
  const fallbackTimeoutRef = useRef(null);

  const { user } = useContext(AuthContext);

  const filteredModuleGroups = useMemo(() => {
    const activatedModules = user?.activatedModules || [];

    // Map backend labels to frontend IDs
    // Note: Backend has "Skeyechart" but frontend ID is "skeyecharts"
    const activeIds = activatedModules.map((mod) => {
      const normalized = mod.label.toLowerCase();
      return normalized === "skeyechart" ? "skeyecharts" : normalized;
    });

    // Helper to filter modules
    const filterGroup = (group) =>
      group.filter((mod) => activeIds.includes(mod.id));

    // => FIX: Call getModuleGroups() here to get the object
    const moduleGroups = getModuleGroups();

    return {
      skeye: filterGroup(moduleGroups.skeye),
      bigData: filterGroup(moduleGroups.bigData),
      governeye: filterGroup(moduleGroups.governeye),
    };
  }, [isDarkTheme, user]);

  const toast = useMemo(
    () => ({
      current: {
        show: ({ severity, summary, detail, key }) => {
          const type =
            severity === "error"
              ? "error"
              : severity === "success"
                ? "success"
                : "info";
          messageApi.open({
            type,
            content: `${summary}: ${detail}`,
            key,
          });
        },
      },
    }),
    [messageApi],
  );

  useEffect(() => {
    const unsubscribe = subscribeToInstancesMinimal(
      null,
      setCardStatus,
      setContainerIds,
      toast,
      TitleMapping,
      setExposedPorts,
      (isConnected) => {
        setWsConnected(isConnected);
        if (isConnected) {
          setIsBackendDown((prev) => {
            if (prev) {
              toast.current.show({
                severity: "success",
                summary: "Connection Restored",
                detail: "Successfully connected to the backend.",
                key: "backend-restore",
              });
            }
            return false;
          });
        }
      },
    );

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [toast, TitleMapping]);

  useEffect(() => {
    if (!wsConnected) {
      const fetchApi = async () => {
        const isSuccess = await getAllInstancesMinimal(
          setCardStatus,
          setContainerIds,
          toast,
          TitleMapping,
          setExposedPorts,
        );

        setIsBackendDown((prev) => {
          if (prev && isSuccess) {
            toast.current.show({
              severity: "success",
              summary: "Connection Restored",
              detail: "Successfully connected to the backend.",
              key: "backend-restore",
            });
          }
          return !isSuccess;
        });
      };

      fallbackTimeoutRef.current = setTimeout(() => {
        fetchApi();
        pollingIntervalRef.current = setInterval(fetchApi, 5000);
      }, 1500);
    } else {
      if (fallbackTimeoutRef.current) clearTimeout(fallbackTimeoutRef.current);
      if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
    }

    return () => {
      if (fallbackTimeoutRef.current) clearTimeout(fallbackTimeoutRef.current);
      if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
    };
  }, [wsConnected, toast, TitleMapping]);

  useEffect(() => {
    if (wsConnected || Object.keys(cardStatus).length > 0) {
      setInitialLoading(false);
    }
  }, [cardStatus, wsConnected]);

  const handleCreate = (moduleId) => {
    const title = TitleMapping[moduleId];
    return createContainer(
      title,
      cardStatus,
      setCardStatus,
      setLoading,
      setContainerIds,
      toast,
      setExposedPorts,
    );
  };

  const handleToggle = (moduleId) => {
    const title = TitleMapping[moduleId];
    return toggleStatus(
      title,
      cardStatus,
      setCardStatus,
      setLoading,
      containerIds,
      setContainerIds,
      toast,
      setExposedPorts,
    );
  };

  const handleDelete = (moduleId) => {
    const title = TitleMapping[moduleId];
    return deleteInstance(
      title,
      containerIds,
      setLoading,
      setContainerIds,
      setCardStatus,
      toast,
      setExposedPorts,
    );
  };

  const sharedGridProps = {
    statuses: cardStatus,
    titleMapping: TitleMapping,
    loading: loading,
    initialLoading: initialLoading,
    onCreate: handleCreate,
    onToggle: handleToggle,
    onDelete: handleDelete,
    isBackendDown: isBackendDown,
  };

  const tabs = useMemo(
    () => [
      {
        key: "skeye",
        label: (
          <span className="tab-label">
            <img
              src={`/img/majesteye-icon-skeye-gate${isDarkTheme ? "-white" : ""}.svg`}
              alt="Skeye"
              className="tab-logo"
            />
            <span className="tab-label-text">Skeye</span>
          </span>
        ),
        children: (
          <ModuleGrid
            modules={filteredModuleGroups.skeye}
            {...sharedGridProps}
          />
        ),
      },
      {
        key: "big-data",
        label: (
          <span className="tab-label">
            <img
              src={`/img/hadoop-logo-no-text${isDarkTheme ? "-white" : ""}.png`}
              alt="Big Data"
              className="tab-logo"
            />
            <span className="tab-label-text">Big Data</span>
          </span>
        ),
        children: (
          <ModuleGrid
            modules={filteredModuleGroups.bigData}
            {...sharedGridProps}
          />
        ),
      },
      {
        key: "governeye",
        label: (
          <span className="tab-label">
            <img
              src={`/img/GovernEYE${isDarkTheme ? "_white" : ""}.png`}
              alt="Governeye"
              className="tab-logo"
            />
            <span className="tab-label-text">Governeye</span>
          </span>
        ),
        children: (
          <ModuleGrid
            modules={filteredModuleGroups.governeye}
            {...sharedGridProps}
          />
        ),
      },
    ],
    [cardStatus, filteredModuleGroups, isBackendDown, isDarkTheme],
  );

  return (
    <div>
      {contextHolder}
      <PageLayout
        title={pageTitle}
        helpContent={homeHelp}
        description="This area is a place where we manage the lifecycle of our data modules, from creation to execution. Each module represents a specific data tool or service that can be spun up in a containerized environment. You can create, start, stop, and delete modules as needed to support your data workflows. Click on the 'Open' button to access the module's interface and start working with your data!"
      >
        <Tabs className="module-tabs" items={tabs} />
      </PageLayout>
    </div>
  );
}

export default Home;
